import Box from '@mui/material/Box';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { HcoIcon } from '@/assets/icons/HcoIcon';
import { Address } from '@/components/Cards/AddressCard/Address/Address';
import { BasicChip } from '@/components/Chips/BasicChip';
import { useProfileMetaData } from '@/components/Hcp/HcpCard/_hooks/useProfileMetaData';
import { CardView } from '@/components/Hcp/HcpCard/HcpCard';
import { ProfileName } from '@/components/Hcp/ProfileName/ProfileName';
import { Separated } from '@/components/Separated/Separated';
import { TEST_IDS } from '@/core/constants/app';
import { Hco } from '@/core/graphql/generated/types';
import { mergeSx } from '@/utils/mergeSx';

import { hcoCardStyles as sx } from './HcoCard.styles';

type Props = {
	hco: Hco;
	withIcon?: boolean;
	withFullAddress?: boolean;
	withoutCountry?: boolean;
	disableLink?: boolean;
	view?: CardView;
	chips?: ReactNode;
	chipLocation?: 'inline' | 'bottom';
	textRows?: string[];
	noWrap?: boolean;
	wrapChips?: boolean;
	query?: string;
	onClick?: () => void;
	nameSlot?: ReactNode;
	customSx?: {
		root?: SxProps<Theme>;
		content?: SxProps<Theme>;
		name?: SxProps<Theme>;
	};
};

export const HcoCard = ({
	hco,
	withIcon = false,
	withFullAddress = false,
	withoutCountry = false,
	disableLink = false,
	noWrap = false,
	wrapChips,
	view = 'default',
	chips,
	chipLocation = 'inline',
	textRows,
	query,
	onClick,
	nameSlot,
	customSx = {}
}: Props) => {
	const theme = useTheme();
	const { t } = useTranslation();
	const { primaryAddress, profileLink, specialtyAndType } =
		useProfileMetaData(hco);

	const hasChips = Array.isArray(chips) ? chips.length > 0 : Boolean(chips);

	return (
		<Box
			sx={mergeSx(sx.root, !disableLink && sx.hoverable, customSx.root)}
			component={disableLink ? Box : Link}
			{...(!disableLink && {
				to: profileLink
			})}
			onClick={onClick}
			aria-label={t('Clickable HCO Search Card')}
			data-testid={TEST_IDS.search.hcoSearchCard}
		>
			{withIcon && (
				<Box sx={sx.iconContainer}>
					<HcoIcon fill={theme.palette.primary.main} />
				</Box>
			)}

			<Box sx={mergeSx(sx.mainContentContainer, customSx.content)}>
				<Box sx={sx.textRow}>
					{nameSlot ?? (
						<ProfileName
							profile={hco as Hco}
							withInternationalName
							inline
							noWrap={noWrap}
							wrapChips={wrapChips}
							withInactive={chipLocation === 'inline'}
							chips={chipLocation === 'inline' && chips}
							query={query}
							customSx={{
								name: mergeSx(sx.name, customSx.name)
							}}
						/>
					)}
				</Box>

				{view === 'compact' && (
					<Separated
						layout='cut'
						separator='•'
						left={specialtyAndType}
						right={
							primaryAddress && (
								<Address
									address={primaryAddress}
									view='summary'
									inline
									withoutCountry={withoutCountry}
									customSx={sx.compactAddress}
								/>
							)
						}
					/>
				)}

				{view === 'default' && (
					<>
						<Box sx={sx.textRow}>
							{specialtyAndType && (
								<Typography variant='body2' sx={sx.address}>
									{specialtyAndType}
								</Typography>
							)}
						</Box>
						{primaryAddress && (
							<Box sx={sx.textRow}>
								<Address
									address={primaryAddress}
									view={withFullAddress ? 'full' : 'summary'}
									inline
									withoutCountry={withoutCountry}
									customSx={sx.address}
								/>
							</Box>
						)}
					</>
				)}

				{textRows?.map(s => (
					<Box key={s} sx={sx.textRow}>
						<Typography variant='body2' sx={sx.address}>
							{s}
						</Typography>
					</Box>
				))}

				{chipLocation === 'bottom' &&
					(hasChips || hco.statusCda_VId === 'iact') && (
						<Box sx={sx.chipRow}>
							{chips}
							{hco.statusCda_VId === 'iact' && (
								<BasicChip label={t('Inactive')} />
							)}
						</Box>
					)}
			</Box>
		</Box>
	);
};
